/**
 * A button that shows an alert dialog for confirmation before firing its action.
 *
 * All standard `Button` props are forwarded to the trigger. The dialog content
 * is configured via `title`, `description`, `confirmLabel`, and `onConfirm`.
 */
import { Loader2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import type { VariantProps } from 'class-variance-authority'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/frontend/components/ui/alert-dialog'
import { Button, type buttonVariants } from '@/frontend/components/ui/button'

export interface ConfirmButtonProps
  extends Omit<React.ComponentProps<typeof Button>, 'onClick'>,
    VariantProps<typeof buttonVariants> {
  /** Dialog heading. */
  heading: string
  /** Dialog body — plain text or rich JSX describing the consequences. */
  description: ReactNode
  /** Label for the confirm action button. Defaults to `"Continue"`. */
  confirmLabel?: string

  /** Controls dialog and confirm button colour. Defaults to `'default'`. */
  dialogColor?: 'default' | 'destructive'
  /** When true, skip the confirmation dialog and fire `onConfirm` immediately on click. */
  bypass?: boolean
  /** When true, the confirm button shows a spinner and is disabled. Use when waiting for background work to finish before confirmation is possible. */
  confirmPending?: boolean
  /** Async check before opening the dialog. If it resolves to false, the dialog stays closed. */
  preValidate?: () => Promise<boolean>
  /** Fired when the user confirms. */
  onConfirm: () => void
}

export function ConfirmButton({
  heading,
  description,
  confirmLabel = 'Continue',
  dialogColor = 'default',
  bypass,
  confirmPending,
  preValidate,
  onConfirm,
  children,
  ...buttonProps
}: ConfirmButtonProps) {
  const [open, setOpen] = useState(false)

  async function handleClick() {
    if (bypass) {
      onConfirm()
      return
    }
    if (preValidate) {
      const valid = await preValidate()
      if (!valid) return
    }
    setOpen(true)
  }

  return (
    <>
      <Button {...buttonProps} onClick={handleClick}>
        {children}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent color={dialogColor}>
          <AlertDialogHeader>
            <AlertDialogTitle>{heading}</AlertDialogTitle>
            <AlertDialogDescription>{description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={dialogColor === 'destructive' ? 'destructive' : undefined}
              onClick={onConfirm}
              disabled={confirmPending}
            >
              {confirmPending && <Loader2 className="size-4 mr-2 animate-spin" />}
              {confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
